import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import withAuth from '../../../../hoc/withAuth'
import OrderService from '../../../../services/order.service'
import { QueueName } from '../../../../types/Order/Queue'
import { OngoingOrder } from '../../../../types/Order/OngoingOrder'
import ProgressBar from '../../../../components/originals/ProgressBar'
import Button, {
  buttonHierarchy,
  buttonShape,
} from '../../../../components/Button'
import routes from '../../../../config/routes'
import { HeroIcons } from '../../../../lib/utils/heroIcons'
import Modal from '../../../../components/Modal'
import { Dialog } from '@headlessui/react'
import Link from 'next/link'
import { Property } from '../../../../types/Property/Property'
import PropertyService from '../../../../services/property.service'
import useLocale from '../../../../hooks/useLocale'
import { getLocalePathFromPathname } from '../../../../lib/utils/language'

const REFRESH_INTERVAL = 5000

const CheckoutQueuePage: any = withAuth((): ReactElement => {
  const { t } = useTranslation('originals/order/queue')
  const router = useRouter()
  const { locale } = useLocale()

  const { propertyId, fractionAmount } = router.query

  const [loading, setLoading] = useState<boolean>(true)
  const [loadingLeaveQueue, setLoadingLeaveQueue] = useState<boolean>(false)
  const [
    leaveQueueConfirmationModalIsOpen,
    setLeaveQueueConfirmationModalIsOpen,
  ] = useState<boolean>(false)
  const [noMoreFraction, setNoMoreFraction] = useState<boolean>(false)

  const [ongoingOrder, setOngoingOrder] = useState<OngoingOrder>()
  const [queueName, setQueueName] = useState<QueueName>()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  if (!fractionAmount) {
    return (
      <>
        <NextSeo
          title={t('seo.title')}
          description={t('seo.description')}
          noindex
        />

        <div className="flex h-full w-full grow items-center">
          <div className="mx-auto space-y-12">
            <HeroIcons.ExclamationTriangleIcon
              className="mx-auto h-28 w-auto rounded-3xl text-feedbackRed"
              aria-hidden="true"
            />
            <div className="flex w-full items-center">
              <div className="mx-auto flex flex-col">
                <h2 className="mx-auto mb-2 flex text-center text-4xl font-medium">
                  {t('orderErrorDisplay.title')}
                </h2>
                <p className="text-center">{t('orderErrorDisplay.subtitle')}</p>
              </div>
            </div>
            <Button
              hierarchy={buttonHierarchy.secondary}
              shape={buttonShape.pill}
              className="mx-auto px-6 py-1"
            >
              <Link href={`${routes.properties}${propertyId}`}>
                {t('orderErrorDisplay.backButton')}
              </Link>
            </Button>
          </div>
        </div>
      </>
    )
  }

  const fetchOrderStatus = async (
    propertyId: string,
  ): Promise<OngoingOrder> => {
    const _ongoingOrder = await OrderService.getOngoingOrder(propertyId)
    if (_ongoingOrder && _ongoingOrder.queueJob) {
      const queueSplit = _ongoingOrder.queueJob.queueName.split('-')
      setQueueName(queueSplit[0] as QueueName)
    }
    return _ongoingOrder
  }

  const fetchProperty = async (propertyId: string): Promise<Property> => {
    return PropertyService.getProperty({ id: propertyId })
  }

  const joinQueue = async (
    propertyId: string,
    desiredFractionQuantity: number,
  ): Promise<void> => {
    setLoading(true)
    try {
      const newOnGoingOrder = await OrderService.joinQueue(
        propertyId,
        desiredFractionQuantity,
      )
      setOngoingOrder(newOnGoingOrder)
      if (newOnGoingOrder && newOnGoingOrder.queueJob) {
        const queueSplit = newOnGoingOrder.queueJob.queueName.split('-')
        setQueueName(queueSplit[0] as QueueName)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const refreshOrderStatus = async (propertyId: string): Promise<void> => {
    const property = await fetchProperty(propertyId as string)
    if (!property) return

    if (property.fractionAvailable < 1) {
      setNoMoreFraction(true)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      return
    }

    const ongoingOrder: OngoingOrder = await fetchOrderStatus(propertyId)
    setOngoingOrder(ongoingOrder)
    if (!ongoingOrder) {
      await joinQueue(propertyId, Number(fractionAmount as string))
    }
    if (ongoingOrder?.stockLock && propertyId) {
      router.replace({
        pathname: routes.checkout,
        query: fractionAmount
          ? {
              fractionAmount: fractionAmount,
              propertyId: propertyId,
            }
          : {
              propertyId: propertyId,
            },
      })
    }
    setLoading(false)
  }

  const leaveQueue = async () => {
    if (!propertyId || !ongoingOrder || !ongoingOrder.queueJob || !queueName)
      return
    setLoadingLeaveQueue(true)

    try {
      await OrderService.leaveQueue(propertyId as string, queueName)
      await router.push(
        getLocalePathFromPathname(routes.properties + `${propertyId}`, locale),
      )
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingLeaveQueue(false)
    }
  }

  const getCurrentPosition = (position: number): number => {
    return position - 1
  }

  useEffect(() => {
    if (!propertyId) return
    refreshOrderStatus(propertyId as string)

    intervalRef.current = setInterval(() => {
      refreshOrderStatus(propertyId as string)
    }, REFRESH_INTERVAL)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [propertyId])

  useEffect(() => {
    if (ongoingOrder && ongoingOrder.stockLock && intervalRef.current) {
      clearInterval(intervalRef.current)
    }
  }, [ongoingOrder])

  const leaveQueueConfirmationModal = (
    <Modal
      isOpen={leaveQueueConfirmationModalIsOpen}
      onClose={() => setLeaveQueueConfirmationModalIsOpen(false)}
    >
      <div className="p-3">
        <div className="pb-3">
          <div className="flex items-start">
            <div className="flex-grow">
              <div className="flex items-center">
                <Dialog.Title
                  as="h3"
                  className="mx-auto text-lg font-medium leading-6 text-gray-900"
                >
                  {t('leaveQueueModal.title')}
                </Dialog.Title>
              </div>
            </div>

            <button onClick={() => setLeaveQueueConfirmationModalIsOpen(false)}>
              <HeroIcons.XMarkIcon className="h-auto w-6" />
            </button>
          </div>
          <p className="text-center text-sm text-gray-500">
            {t('leaveQueueModal.subtitle')}
          </p>
        </div>
        <div className="flex items-center">
          <button
            type="button"
            className="mx-auto inline-flex w-full justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 md:w-auto"
            onClick={leaveQueue}
          >
            {t('leaveQueueModal.button')}
          </button>
        </div>

        <div className="flex items-center pt-1">
          <p className="mx-auto text-xs font-thin">
            {t('leaveQueueModal.note')}
          </p>
        </div>
      </div>
    </Modal>
  )

  if (loadingLeaveQueue) {
    return (
      <>
        <NextSeo title={t('seo.title')} description={t('seo.description')} />
        <div className="flex h-full w-full items-center">
          <div className="mx-auto space-y-12">
            <img
              className="mx-auto rounded-3xl"
              src="/img/logos/shelters/logo-colourful-min.png"
              alt="Shelters"
            />
            <div className="flex w-full items-center">
              <div className="mx-auto flex flex-col">
                <h2 className="mx-auto mb-2 flex text-center text-2xl font-medium md:text-4xl">
                  {t('leavingQueueDisplay.title')}
                  <span className="ellipsis"></span>
                </h2>
                <p className="text-center">
                  {t('leavingQueueDisplay.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (noMoreFraction) {
    return (
      <>
        <NextSeo title={t('seo.title')} description={t('seo.description')} />

        <div className="flex h-full w-full items-center">
          <div className="mx-auto space-y-12">
            <HeroIcons.FaceFrownIcon
              className="mx-auto h-28 w-auto rounded-3xl text-feedbackYellow"
              aria-hidden="true"
            />
            <div className="flex w-full items-center">
              <div className="mx-auto flex flex-col">
                <h2 className="mx-auto mb-2 flex text-center text-4xl font-medium">
                  {t('noMoreFractionDisplay.title')}
                </h2>
                <p className="text-center">
                  {t('noMoreFractionDisplay.subtitle')}
                </p>
              </div>
            </div>
            <Button
              hierarchy={buttonHierarchy.secondary}
              shape={buttonShape.pill}
              className="mx-auto px-6 py-1"
            >
              <Link href={`${routes.properties}${propertyId}`}>
                {t('noMoreFractionDisplay.backButton')}
              </Link>
            </Button>
          </div>
        </div>
      </>
    )
  } else if (
    loading ||
    !propertyId ||
    (ongoingOrder &&
      ongoingOrder.queueJob &&
      ongoingOrder.queueJob.queueName &&
      queueName &&
      queueName === QueueName.OrderRoute)
  ) {
    return (
      <>
        <NextSeo title={t('seo.title')} description={t('seo.description')} />
        <div className="flex h-full w-full items-center">
          <div className="mx-auto space-y-12">
            <img
              className="mx-auto rounded-3xl"
              src="/img/logos/shelters/logo-colourful-min.png"
              alt="Shelters"
            />
            <div className="flex w-full items-center">
              <div className="mx-auto flex flex-col">
                <h2 className="mx-auto mb-2 flex text-center text-2xl font-medium md:text-4xl">
                  {t('joiningQueueDisplay.title')}
                  <span className="ellipsis"></span>
                </h2>
                <p className="text-center">
                  {t('joiningQueueDisplay.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (!ongoingOrder) {
    return (
      <>
        <NextSeo title={t('seo.title')} description={t('seo.description')} />

        <div className="flex h-full w-full items-center">
          <div className="mx-auto space-y-12">
            <HeroIcons.ExclamationTriangleIcon
              className="mx-auto h-28 w-auto rounded-3xl text-feedbackRed"
              aria-hidden="true"
            />
            <div className="flex w-full items-center">
              <div className="mx-auto flex flex-col">
                <h2 className="mx-auto mb-2 flex text-center text-4xl font-medium">
                  {t('orderErrorDisplay.title')}
                </h2>
                <p className="text-center">{t('orderErrorDisplay.subtitle')}</p>
              </div>
            </div>
            <Button
              hierarchy={buttonHierarchy.secondary}
              shape={buttonShape.pill}
              className="mx-auto px-6 py-1"
            >
              <Link href={`${routes.properties}${propertyId}`}>
                {t('orderErrorDisplay.backButton')}
              </Link>
            </Button>
          </div>
        </div>
      </>
    )
  } else if (
    ongoingOrder &&
    ongoingOrder.queueJob &&
    ongoingOrder.queueJob.queueName &&
    queueName &&
    queueName === QueueName.StockLock
  ) {
    return (
      <>
        <NextSeo title={t('seo.title')} description={t('seo.description')} />

        {leaveQueueConfirmationModal}
        <div className="flex h-full w-full flex-col">
          <div className="flex flex-grow items-center">
            <div className="mx-auto space-y-3 sm:space-y-12">
              <img
                className="mx-auto rounded-3xl"
                src="/img/logos/shelters/logo-colourful-min.png"
                alt="Shelters"
              />

              <div className="flex w-full items-center">
                <div className="mx-auto flex flex-col">
                  <h2 className="mx-auto mb-2 flex text-center text-2xl font-medium md:text-4xl">
                    {t('inLineDisplay.title')}
                    <span className="ellipsis"></span>
                  </h2>
                  <p className="text-center">{t('inLineDisplay.subtitle')}</p>
                </div>
              </div>

              <div>
                <ProgressBar
                  fractionCount={ongoingOrder.fractionCount}
                  fractionFunded={ongoingOrder.fractionFunded}
                  id={propertyId as string}
                  hidePercent
                  hideTooltip
                  lineHeight={4}
                />
                <p className="mt-1 text-center text-lg font-medium">
                  {ongoingOrder.fractionCount - ongoingOrder.fractionFunded}{' '}
                  {t('inLineDisplay.fractionsLeft')}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="rounded-2xl bg-brandLightBlue p-6">
                  <h2 className="text-md text-center font-normal text-neutralWhite">
                    {t('inLineDisplay.investorAhead')}
                  </h2>
                  {queueName && queueName === QueueName.StockLock ? (
                    <p className="text-center text-3xl font-bold text-neutralWhite">
                      {getCurrentPosition(ongoingOrder.queueJob.position)
                        ? getCurrentPosition(ongoingOrder.queueJob.position)
                        : t('inLineDisplay.youAreNext')}
                    </p>
                  ) : (
                    <HeroIcons.ArrowPathIcon className="mx-auto mt-1 h-8 w-8 animate-spin text-neutralWhite" />
                  )}
                </div>
                <div className="p-6">
                  <h2 className="text-md text-center font-normal">
                    {t('inLineDisplay.fractionsInquired')}
                  </h2>
                  <p className="text-center text-3xl font-bold">
                    {ongoingOrder.queueJob.fractionExpected}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-center text-sm">
                  <strong>{t('inLineDisplay.note.title')}</strong>{' '}
                  {t('inLineDisplay.note.text')}
                </p>
              </div>

              {queueName && queueName === QueueName.StockLock && (
                <div>
                  <Button
                    hierarchy={buttonHierarchy.custom}
                    shape={buttonShape.pill}
                    className="mx-auto bg-red-600 px-6 py-1 text-neutralWhite"
                    onClick={() => setLeaveQueueConfirmationModalIsOpen(true)}
                  >
                    {t('inLineDisplay.leaveQueue')}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full items-center">
            <div className="mx-auto">
              <p className="text-center text-xs font-light">
                {t('inLineDisplay.joinedAt')}{' '}
                {new Date(ongoingOrder.queueJob.createdAt).toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      </>
    )
  } else if (ongoingOrder.stockLock) {
    return (
      <>
        <NextSeo title={t('seo.title')} description={t('seo.description')} />

        <div className="flex h-full w-full items-center">
          <div className="mx-auto space-y-12">
            <img
              className="mx-auto rounded-3xl"
              src="/img/logos/shelters/logo-colourful-min.png"
              alt="Shelters"
            />
            <div className="flex w-full items-center">
              <div className="mx-auto flex flex-col">
                <h2 className="mx-auto mb-2 flex text-center text-2xl font-medium md:text-4xl">
                  {t('lockAcquiredDisplay.title')}
                  <span className="ellipsis"></span>
                </h2>
                <p className="text-center">
                  {t('lockAcquiredDisplay.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return <></>
})

CheckoutQueuePage.getLayout = (page: ReactElement) => {
  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-slate-100 px-5">
      {page}
    </div>
  )
}

export default CheckoutQueuePage

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'originals/global',
        'originals/order/queue',
      ])),
    },
  }
}
